import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CommonImage from '@components/CommonImage';
import LoaderComponent from '@components/LoaderComponent';

import FavouriteFoldersList from '@features/favourites/components/FavouriteFoldersList';
import MaterialFavourite from '@features/favourites/components/MaterialFavourite';
import SortingDropdown from '@features/favourites/components/SortingDropdown';
import {
	FAVOURITE_SELECT_VIEW,
	FAVOURITES_FOLDER_SORTING_OPTIONS
} from '@features/favourites/utils/favouriteUtil';

import { fetchFavouriteMaterials } from '@store/favourite/actions';
import { getFavouriteMaterials } from '@store/reducers';

import { getFolderListWithDetails } from '@services/favouriteService';

import { isPageReachBottom, printApiError } from '@utils/Util';

import { resetLikeCount } from '@/features/favourites/redux/favouriteSlice';

const Favourite = () => {
	const [isLoading, setIsLoading] = useState(false);
	const [selectView, setSelectView] = useState(FAVOURITE_SELECT_VIEW.STYLE.key);
	const [favouriteFolders, setFavouriteFolders] = useState([]);
	const [isAddNewFolder, setIsAddNewFolder] = useState(false);
	const [materialsDataList, setMaterialsDataList] = useState({});
	const [selectedMaterialIdList, setSelectedMaterialIdList] = useState([]);
	const [allFavouritesForMaterials, setAllFavouritesForMaterials] = useState({});
	const [searchForFabric, setSearchForFabric] = useState('');
	const [selectedSortingOption, setSelectedSortingOption] = useState({
		code: 'NEWEST',
		title: 'Newest first'
	});

	useEffect(() => {
		dispatch(resetLikeCount());
	}, []);

	useEffect(() => {
		fetchFavouriteFolders();
	}, [selectedSortingOption?.code]);

	let favouritesForMaterials = useSelector(getFavouriteMaterials);

	const scrollRef = useRef({});
	const dispatch = useDispatch();

	useEffect(() => {
		initApiCall().finally(() => setIsLoading(false));
	}, [selectedSortingOption?.code]);

	useEffect(() => {
		if (favouritesForMaterials?.data?.length > 0) {
			setAllFavouritesForMaterials(favouritesForMaterials);
		}
		scrollRef.current = favouritesForMaterials;
	}, [favouritesForMaterials]);

	const fetchFavouriteFolders = async (searchInput = '', page = 0) => {
		try {
			setIsLoading(true);
			const params = `?page=${page}&size=20&sortBy=${selectedSortingOption?.code}&search=${searchInput}`;
			const response = await getFolderListWithDetails(params);
			if (page > 0) {
				setFavouriteFolders((prev) => ({
					...prev,
					data: [...prev?.data, ...response?.data]
				}));
			} else {
				setFavouriteFolders(response);
			}
			setIsLoading(false);
		} catch (error) {
			printApiError(error);
		} finally {
			setIsLoading(false);
		}
	};

	const initApiCall = async () => {
		setIsLoading(true);
		await dispatch(fetchFavouriteMaterials(0, 30)).finally(() => {});
	};

	const handleSearchFolder = async (e) => {
		let { value } = e.target;
		setIsLoading(true);
		fetchFavouriteFolders(value);
	};

	const onChangeAllCheckAndSearchForFabric = async (e) => {
		let { name, value } = e.target;
		if (name === 'searchForFabric') {
			setSearchForFabric(value);
			setIsLoading(true);
			dispatch(fetchFavouriteMaterials(0, 30, value)).finally(() => {
				setIsLoading(false);
			});
		}
	};

	const handleScroll = () => {
		if (isPageReachBottom()) {
			let { currentPage, totalElements, totalPages, data } = scrollRef.current;
			if (totalElements !== data.length && currentPage + 1 !== totalPages) {
				setIsLoading(true);
				dispatch(fetchFavouriteMaterials(currentPage + 1, 30)).finally(() => {
					setIsLoading(false);
				});
			}
		}
	};

	useEffect(() => {
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const topFilterForStyle = () => {
		return (
			<div className="d-flex justify-content-between pb-1">
				<div className="filter-actions collection-details-filter favorites-filter">
					<div className="form-group size32 m-0 search-filter filter-with-animation">
						<div className={'with-icon right-icon'}>
							<input
								type="text"
								placeholder="Folder name"
								name="searchForStyle"
								onChange={handleSearchFolder}
								autoComplete="off"
								autoFocus={false}
							/>
							<span className="right-icon">
								<CommonImage src="/icons/search.png" alt="search" />
							</span>
						</div>
					</div>

					<SortingDropdown
						onSelectOption={setSelectedSortingOption}
						selectedOption={selectedSortingOption?.title}
						sortingOptions={FAVOURITES_FOLDER_SORTING_OPTIONS}
					/>
				</div>
				<div className="add-new-design">
					<button className="button text size32 px-0" onClick={() => setIsAddNewFolder(true)}>
						<CommonImage src="/icons/add-round.svg" alt="add" />
						<span className="ml-1">Folder</span>
					</button>
				</div>
			</div>
		);
	};

	const topFilterForFabric = () => {
		return (
			<div className="filter-actions">
				<div className="form-group size32 m-0 search-filter filter-with-animation">
					<div className={'with-icon right-icon'}>
						<input
							type="text"
							autoComplete="off"
							name="searchForFabric"
							placeholder={'Name & Ref number'}
							onChange={onChangeAllCheckAndSearchForFabric}
						/>
						<span className="right-icon">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width={23}
								height={22}
								viewBox="0 0 23 22"
								fill="none"
							>
								<path
									d="M10.543 16.5C14.3399 16.5 17.418 13.422 17.418 9.625C17.418 5.82804 14.3399 2.75 10.543 2.75C6.74601 2.75 3.66797 5.82804 3.66797 9.625C3.66797 13.422 6.74601 16.5 10.543 16.5Z"
									stroke="#21242B"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M19.2526 18.3346L15.5859 14.668"
									stroke="#21242B"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</span>
					</div>
				</div>
			</div>
		);
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="top-header bg-white">
				<div className="page-title-area pb-2">
					<h1 className="semibold-16">FAVOURITE</h1>
				</div>
				<div className="filter-container-top">
					<div className="inner-tabs">
						<ul>
							<li
								className={selectView === FAVOURITE_SELECT_VIEW.STYLE.key ? 'active' : ''}
								onClick={() => {
									setSelectView(FAVOURITE_SELECT_VIEW.STYLE.key);
								}}
							>
								Folder (
								{favouriteFolders?.totalElements ? favouriteFolders?.totalElements : 0})
							</li>
							<li
								className={selectView === FAVOURITE_SELECT_VIEW.FABRIC.key ? 'active' : ''}
								onClick={() => {
									setSelectView(FAVOURITE_SELECT_VIEW.FABRIC.key);
								}}
							>
								Fabric (
								{allFavouritesForMaterials?.data?.length > 0
									? allFavouritesForMaterials?.data?.length
									: 0}
								)
							</li>
						</ul>
					</div>
					{selectView === FAVOURITE_SELECT_VIEW.STYLE.key
						? topFilterForStyle()
						: selectView === FAVOURITE_SELECT_VIEW.FABRIC.key
							? topFilterForFabric()
							: ''}
				</div>
			</div>
			{selectView === FAVOURITE_SELECT_VIEW.STYLE.key ? (
				<FavouriteFoldersList
					favouriteFolders={favouriteFolders}
					fetchFavouriteFolders={fetchFavouriteFolders}
					isAddNewFolder={isAddNewFolder}
					setIsAddNewFolder={setIsAddNewFolder}
				/>
			) : selectView === FAVOURITE_SELECT_VIEW.FABRIC.key ? (
				<MaterialFavourite
					setIsLoading={setIsLoading}
					isLoading={isLoading}
					materialsDataList={materialsDataList}
					setMaterialsDataList={setMaterialsDataList}
					materialsData={allFavouritesForMaterials}
					setAllFavouritesForMaterials={setAllFavouritesForMaterials}
					selectedMaterialIdList={selectedMaterialIdList}
					setSelectedMaterialIdList={setSelectedMaterialIdList}
					search={searchForFabric}
				/>
			) : (
				''
			)}
		</LoaderComponent>
	);
};

export default Favourite;
